import { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import { setUser } from '@sentry/react';
import i18n from 'i18n';
// Types
import UserRoles from 'app/types/UserRoles';
import BodyErrors from "app/types/BodyErrors";
// Models
import { AppDispatch } from 'app/store';
import { IMyUser } from 'app/models/User';
// Async
import { logout } from 'app/store/Auth/Auth.async';
import { resetPassword, getCurrentUser } from 'app/store/Users/Users.async';
import { getCurrentAccount, getUserAccounts } from 'app/store/Accounts/Accounts.async';
import { getSubscriptions } from 'app/store/BillingSubscriptions/BillingSubscriptions.async';
import { getAccounts } from 'app/store/Clients/Clients.async';
import { getUnseenConversations } from "app/store/Conversations/Conversations.async";
import { getMedicareCarriers } from "app/store/Medical/Medical.async";
import { getVariables } from "app/store/Templates/Templates.async";
import { getSettings } from "app/store/Presenter/Presenter.async";
import { getIntegrations } from 'app/store/Integrations/Integrations.async';
import { getCollections } from 'app/store/DMSCollections/DMSCollections.async';
import { getGeneralSettings } from 'app/store/Settings/Settings.async';
import { getInvoices } from 'app/store/BillingInvoices/BillingInvoices.async';
// Actions
import { AuthActions } from 'app/store/Auth/Auth.slice';
import { BillingInvoicesActions } from 'app/store/BillingInvoices/BillingInvoices.slice';
import { AppUiNotificationsActions } from 'app/store/AppUINotifications/AppUINotifications.slice';
// Selectors
import { selectParams } from 'app/store/BillingInvoices/BillingInvoices.selectors';
// Services
import Mixpanel from "app/services/Mixpanel.service";
import { selectBillingOverdueWarningsHidden } from 'app/store/Accounts/Accounts.selectors';

const UsersMiddleware:Middleware = ({ getState, dispatch }:MiddlewareAPI<AppDispatch>) => (next:any) => async (action:any) => {
  const { type, payload } = action;

  if ( type === resetPassword.rejected.type ){
    const { error } = payload;
    if ( error === BodyErrors.AccountRequired ){
      const { accounts, message } = payload;
      dispatch(AuthActions.setMultiAccountsData({ accounts, message }));
    }
  }

  if ( type === getCurrentUser.fulfilled.type ){
    if ( window.location.pathname === '/email-verification' ){
      dispatch(logout());
      return next(action);
    }

    const myUser = payload as IMyUser;

    const isMyUserRoleAdmin = myUser.role === UserRoles.Admin;
    const isMyUserRoleStaff = myUser.role === UserRoles.Staff;
    const isMyUserRolePresenter = myUser.role === UserRoles.Presenter;
    const isMyUserRoleAM = myUser.role === UserRoles.AccountManager;

    Mixpanel.identify(myUser.id);

    if ( isMyUserRoleAdmin || isMyUserRoleStaff || isMyUserRoleAM ){
      dispatch(getIntegrations({ service: 'quickBooks' }));
    }

    if ( isMyUserRoleAdmin || isMyUserRoleStaff ){
      if ( isMyUserRoleAdmin ){
        dispatch(getSubscriptions({}));
      }

      dispatch(getGeneralSettings({}));
      dispatch(getCurrentAccount({}));
      dispatch(getUserAccounts({}));
      dispatch(getAccounts({}));
      dispatch(getUnseenConversations({}));
      dispatch(getMedicareCarriers({}));
      dispatch(getVariables({}));
      dispatch(getCollections({}));
    }

    if ( isMyUserRolePresenter ){
      dispatch(getSettings({}));
    }

    if (isMyUserRoleAdmin) {
      dispatch(getInvoices({ overdue: true, limit: 0 })).unwrap()
        .then((invoices) => {
          if (invoices.total) {
            const state = getState();
            const invoicesParams = selectParams(state);
            const billingOverdueWarningsHidden = selectBillingOverdueWarningsHidden(state);
            if (!billingOverdueWarningsHidden){
              dispatch(AppUiNotificationsActions.addSystemMessage({
                type: 'warning',
                message: i18n.t('notifications.usersMiddleware.overdueInvoices'),
                forceShow: true,
                actionButton: {
                  label: i18n.t('notifications.usersMiddleware.goToBillingButton'),
                  to: '/admin/billing-and-subscriptions',
                  onClick: () => {
                    dispatch(BillingInvoicesActions.setParams({ status: 'sent', overdue: true }));
                    dispatch(getInvoices({ ...invoicesParams, status: 'sent', overdue: true }));
                  }
                }
              }));
            }
          }
        });
    }

    dispatch(AuthActions.setAuthenticated(true));
    dispatch(AuthActions.setInitialField('multiFactory'));
    dispatch(AuthActions.setInitialField('multiAccounts'));

    // Set user for sentry
    setUser({ id: myUser.id });
  } else if ( type === getCurrentUser.rejected.type ){
    dispatch(logout());
  }

  return next(action);
}

export default UsersMiddleware;